import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Coins, Swords, Flag, Users, ChevronLeft, ChevronRight, Eye, Zap } from "lucide-react";

const PAGE = 4;
const KINDS = { all: "All", arena: "Arena (AI)", race: "Races (AI)", lobby: "Open Lobbies" };

// Surfaces everything you can currently bet on, with correct links to the bet page.
export function BettableNow() {
  const navigate = useNavigate();
  const [kind, setKind] = useState("all");
  const [page, setPage] = useState(0);

  const { data: arena } = useQuery({
    queryKey: ["bet-arena"],
    queryFn: async () => (await api.get("/arena/spectator/matches")).data,
    refetchInterval: 5000,
  });
  const { data: races } = useQuery({
    queryKey: ["bet-races"],
    queryFn: async () => (await api.get("/race/spectator/list")).data,
    refetchInterval: 5000,
  });
  const { data: lobbies } = useQuery({
    queryKey: ["bet-lobbies"],
    queryFn: async () => (await api.get("/race/open")).data,
    refetchInterval: 5000,
  });

  const items = useMemo(() => {
    const out = [];
    const arenaList = arena?.live || [];
    for (const m of arenaList) {
      const s = m.sides || [];
      out.push({
        kind: "arena", id: m.id,
        label: s.length >= 2 ? `${s[0]?.lead || "?"} vs ${s[1]?.lead || "?"}` : "AI Arena match",
        open: (m.status === "betting"), status: m.status,
        pool: m.total_pool ?? 0,
        to: `/arena/watch/${m.id}`,
      });
    }
    const raceList = races?.live || [];
    for (const r of raceList) {
      out.push({
        kind: "race", id: r.id,
        label: `AI Gauntlet · ${(r.racers?.length || r.lanes?.length || 0)} racers`,
        open: (r.status === "betting"), status: r.status,
        pool: r.total_pool ?? 0,
        to: `/race/watch/${r.id}`,
      });
    }
    for (const l of (lobbies || [])) {
      out.push({
        kind: "lobby", id: l.id,
        label: `Lobby · ${l.racers?.length || 0}/${l.slots} racers`,
        open: l.status === "open", status: l.status,
        pool: l.total_pool ?? 0,
        to: `/race/${l.id}`,
      });
    }
    // open-for-betting first, then by pool size
    out.sort((a, b) => (b.open - a.open) || (b.pool - a.pool));
    return kind === "all" ? out : out.filter((i) => i.kind === kind);
  }, [arena, races, lobbies, kind]);

  const pageCount = Math.max(1, Math.ceil(items.length / PAGE));
  const safePage = Math.min(page, pageCount - 1);
  const shown = items.slice(safePage * PAGE, safePage * PAGE + PAGE);

  const icon = (k) => (k === "arena" ? Swords : k === "race" ? Flag : Users);

  return (
    <div className="mb-2 rounded-lg border border-amber-400/20 bg-amber-500/[0.05] p-2" data-testid="bettable-now">
      <div className="flex items-center gap-2 mb-2">
        <Zap className="h-3.5 w-3.5 text-amber-300" />
        <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-amber-300/80 mr-auto">Bettable now</span>
        <Select value={kind} onValueChange={(v) => { setKind(v); setPage(0); }}>
          <SelectTrigger data-testid="bettable-filter" className="h-7 w-32 bg-[hsl(220_18%_12%)] border-white/10 text-white text-[11px]"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[hsl(222_22%_8%)] border-white/10 text-white">
            {Object.entries(KINDS).map(([k, v]) => <SelectItem key={k} value={k} className="text-xs">{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {items.length === 0 ? (
        <div className="text-white/40 text-xs py-3 text-center" data-testid="bettable-empty">Nothing to bet on right now. Spin up a Spectator match!</div>
      ) : (
        <>
          <div className="space-y-1.5">
            {shown.map((it) => {
              const Icon = icon(it.kind);
              return (
                <div key={`${it.kind}-${it.id}`} data-testid={`bettable-${it.id}`}
                  className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/30 p-2">
                  <Icon className="h-4 w-4 text-cyan-300 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-white text-xs truncate">{it.label}</div>
                    <div className="flex items-center gap-2 font-mono text-[9px] mt-0.5">
                      {it.open ? <span className="text-emerald-300 uppercase tracking-[0.15em]">● open</span> : <span className="text-white/40 uppercase tracking-[0.15em]">in progress</span>}
                      {it.pool > 0 && <span className="text-amber-300 inline-flex items-center gap-0.5"><Coins className="h-2.5 w-2.5" /> {Number(it.pool).toLocaleString()}</span>}
                    </div>
                  </div>
                  <Button size="sm" data-testid={`bettable-go-${it.id}`} onClick={() => navigate(it.to)}
                    className={`h-7 text-xs shrink-0 ${it.open ? "bg-amber-300 text-black hover:bg-amber-200" : "bg-white/10 text-white hover:bg-white/15"}`}>
                    {it.open ? <><Coins className="h-3.5 w-3.5 mr-1" /> Bet</> : <><Eye className="h-3.5 w-3.5 mr-1" /> Watch</>}
                  </Button>
                </div>
              );
            })}
          </div>
          {items.length > PAGE && (
            <div className="flex items-center justify-between mt-2">
              <span className="font-mono text-[9px] text-white/35">{items.length} · {safePage + 1}/{pageCount}</span>
              <div className="flex gap-1">
                <button data-testid="bettable-prev" disabled={safePage === 0} onClick={() => setPage(safePage - 1)} className="h-6 w-6 rounded border border-white/15 text-white/60 disabled:opacity-30 flex items-center justify-center"><ChevronLeft className="h-3.5 w-3.5" /></button>
                <button data-testid="bettable-next" disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)} className="h-6 w-6 rounded border border-white/15 text-white/60 disabled:opacity-30 flex items-center justify-center"><ChevronRight className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
